/** Shared constants and helpers for BPM approval flow preview / designer. */

export const GLOBAL_ABSENCE_TASK_QUERY = Object.freeze({
  name: 'Global Absence Recording - Single Assignment',
  code: '30000003500603',
  category: 'absence',
});

export const APPROVER_ACTOR_OPTIONS = Object.freeze([
  { value: 'LineManager', label: 'Line Manager' },
  { value: 'HR_ABSENCE', label: 'HR Absence Specialist Group' },
]);

/** Default rule segments for edit mode (absence reference). */
export const DEFAULT_RULE_SEGMENTS = Object.freeze([
  { id: 1, condition: 'Absence duration > 3', actors: ['LineManager', 'HR_ABSENCE'] },
  { id: 2, condition: 'Absence duration <= 3', actors: ['LineManager'] },
]);

export const ABSENCE_REFERENCE_EXTRACT = Object.freeze({
  source: 'reference',
  task: {
    name: GLOBAL_ABSENCE_TASK_QUERY.name,
    code: GLOBAL_ABSENCE_TASK_QUERY.code,
    category: GLOBAL_ABSENCE_TASK_QUERY.category,
    taskKey: 'global-absence-recording-single-assignment',
  },
  message:
    'Reference absence rule flow from product catalog — live extract API unavailable. Ensure API is on port 3010 and client proxy matches.',
  conditions: [
    {
      badgeLabel: 'Absence duration > 3',
      raw: 'Task.payload.AbsenceDays > 3',
      field: 'AbsenceDays',
      operator: '>',
      value: '3',
    },
    {
      badgeLabel: 'Absence duration <= 3',
      raw: 'Task.payload.AbsenceDays <= 3',
      field: 'AbsenceDays',
      operator: '<=',
      value: '3',
    },
  ],
  flow: {
    nodes: [
      { id: 'flow-start', type: 'start', data: { label: 'Start' } },
      {
        id: 'rule-0',
        type: 'rule',
        data: {
          label: 'Absence duration > 3',
          badgeLabel: 'Absence duration > 3',
          raw: 'Task.payload.AbsenceDays > 3',
        },
      },
      {
        id: 'action-0',
        type: 'action',
        data: { label: 'LineManager, HR_ABSENCE', actionType: 'SequentialApproval' },
      },
      {
        id: 'gw-0',
        type: 'gateway',
        data: { label: 'Next rule' },
      },
      {
        id: 'rule-1',
        type: 'rule',
        data: {
          label: 'Absence duration <= 3',
          badgeLabel: 'Absence duration <= 3',
          raw: 'Task.payload.AbsenceDays <= 3',
        },
      },
      {
        id: 'action-1',
        type: 'action',
        data: { label: 'LineManager', actionType: 'SequentialApproval' },
      },
    ],
    edges: [],
  },
});

function parseActorList(label) {
  if (!label) return [];
  return String(label)
    .split(/,\s*/)
    .map((a) => a.trim())
    .filter(Boolean);
}

function segmentFromSqlRule(rule, index) {
  const condition =
    rule.condition?.badgeLabel ||
    rule.approvalCondition ||
    rule.condition?.raw ||
    rule.condition_text ||
    '';
  const actors = parseActorList(rule.listBuilder || rule.action);
  return {
    id: index + 1,
    condition,
    liveCondition: rule.approvalCondition || rule.condition?.raw || condition,
    actors: actors.length ? actors : ['LineManager'],
    ruleName: rule.name || rule.rule_name,
    priority: rule.priority ?? index + 1,
    actionType: rule.routingParameters || rule.action_type || 'SequentialApproval',
    rulesetName: rule.rulesetName || rule.ruleset_name || null,
  };
}

/**
 * Derive editable rule segments from configured-approvals meta (SQL rules or flow nodes).
 * @returns {Array<{ id: number, condition: string, actors: string[], ruleName?: string, priority?: number }>|null}
 */
export function segmentsFromMeta(meta) {
  const sqlRules = meta?.rules || meta?.arrays?.rules || [];
  if (meta?.source === 'sql' && sqlRules.length) {
    return sqlRules.map((r, i) => segmentFromSqlRule(r, i));
  }

  const nodes = meta?.flow?.nodes || [];
  const edges = meta?.flow?.edges || [];
  if (!nodes.length) return null;

  const nodeById = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const ruleNodes = nodes.filter((n) => n.type === 'rule' && n.data?.label !== 'Start');
  const segments = [];

  for (const rule of ruleNodes) {
    const outEdge = edges.find((e) => e.source === rule.id);
    const target = outEdge ? nodeById[outEdge.target] : null;
    let actors = [];
    if (target?.type === 'action') {
      actors = parseActorList(target.data?.label);
    }
    const condition = rule.data?.badgeLabel || rule.data?.label || '';
    if (!condition) continue;
    segments.push({
      id: segments.length + 1,
      condition,
      liveCondition: rule.data?.raw || condition,
      actors: actors.length ? actors : ['LineManager'],
      actionType: target?.data?.actionType || 'SequentialApproval',
    });
  }

  if (segments.length) return segments;

  const conditions = meta?.conditions || [];
  const actions = meta?.actions || meta?.arrays?.actions || [];
  if (!conditions.length) return null;

  return conditions.map((c, i) => {
    const act = actions[i];
    const label = act?.label || act?.actionType || act?.raw;
    return {
      id: i + 1,
      condition: c.badgeLabel || c.raw || '',
      liveCondition: c.raw || c.badgeLabel || '',
      actors: parseActorList(label).length ? parseActorList(label) : ['LineManager'],
      actionType: act?.actionType || 'SequentialApproval',
    };
  });
}

/** Ruleset name from SQL rules or task composite. */
export function resolveRulesetName(meta, taskQuery = {}) {
  const rules = meta?.rules || meta?.arrays?.rules || [];
  for (const r of rules) {
    const name = r.rulesetName || r.ruleset_name;
    if (name) return name;
  }
  const firstRow = meta?.sqlRows?.[0];
  if (firstRow?.ruleset_name || firstRow?.RULESET_NAME) {
    return firstRow.ruleset_name || firstRow.RULESET_NAME;
  }
  if (taskQuery?.name) {
    return String(taskQuery.name).replace(/\s+/g, '');
  }
  return meta?.task?.taskKey || 'GlobalAbsenceRecording';
}

/**
 * Oracle-compatible update payload for future AMX/Fusion deploy.
 * @param {Array} ruleSegments
 * @param {object} meta
 * @param {object} [taskQuery]
 */
export function buildOracleUpdatePayload(ruleSegments, meta, taskQuery = {}) {
  const ruleset_name = resolveRulesetName(meta, taskQuery);
  const sqlRules = meta?.rules || meta?.arrays?.rules || [];

  const rules = (ruleSegments || []).map((seg, i) => {
    const sqlRule = sqlRules[i] || {};
    const conditionText = seg.liveCondition || seg.condition || '';
    return {
      rule_name: seg.ruleName || sqlRule.name || sqlRule.rule_name || `Rule ${i + 1}`,
      priority: seg.priority ?? sqlRule.priority ?? i + 1,
      condition_text: conditionText,
      live_condition: conditionText,
      action_type: seg.actionType || sqlRule.routingParameters || 'SequentialApproval',
      parameter_values: (seg.actors || []).join(','),
    };
  });

  return {
    ruleset_name,
    rules,
    task: meta?.task?.name || taskQuery?.name || null,
    source: meta?.source || null,
  };
}

export function cloneDefaultSegments() {
  return DEFAULT_RULE_SEGMENTS.map((s) => ({
    ...s,
    actors: [...s.actors],
  }));
}

const OPERATOR_TOKENS = ['>=', '<=', '==', '!=', '>', '<'];

/** Parse a raw/badge condition into { description, attribute, operator, value }. */
export function parseConditionToParts(condition, liveCondition) {
  const raw = String(liveCondition || condition || '').trim();
  const description = String(condition || liveCondition || '').trim();
  if (!raw) return { description, attribute: '', operator: '>', value: '' };

  // e.g. "Task.payload.AbsenceDays > 3" or "Absence duration > 3"
  const cleaned = raw.replace(/^task\.payload\./i, '').replace(/^task\./i, '');
  for (const op of OPERATOR_TOKENS) {
    const idx = cleaned.indexOf(op);
    if (idx > -1) {
      const attribute = cleaned.slice(0, idx).trim().replace(/\s+/g, '');
      const value = cleaned.slice(idx + op.length).trim().replace(/^['"]|['"]$/g, '');
      return { description, attribute, operator: op, value };
    }
  }
  return { description, attribute: cleaned, operator: '>', value: '' };
}

/**
 * Full set of Fusion participant types a user can choose in the THEN editor.
 * `needsValue` controls whether a value/ref input is shown.
 */
export const PARTICIPANT_TYPE_OPTIONS = Object.freeze([
  { value: 'managementHierarchy', label: 'Management Hierarchy', needsValue: false, isHierarchy: true },
  { value: 'jobLevelLineManager', label: 'Job-Level Line Manager Hierarchy', needsValue: false, isHierarchy: true },
  { value: 'positionHierarchy', label: 'Position Hierarchy', needsValue: false, isHierarchy: true },
  { value: 'representative', label: 'Representative', needsValue: true, valueLabel: 'Representative type (e.g. HD_HR_REP)' },
  { value: 'approvalGroup', label: 'Approval Group', needsValue: true, valueLabel: 'Group name' },
  { value: 'applicationRole', label: 'Application Role', needsValue: true, valueLabel: 'Role name' },
  { value: 'user', label: 'Users', needsValue: true, valueLabel: 'User name(s)' },
  { value: 'selfAutoApprove', label: 'Self Auto Approve', needsValue: false },
]);

/** Hierarchy "type" options seen in the tenant (Add Hierarchy Participant dialog). */
export const HIERARCHY_TYPE_OPTIONS = Object.freeze([
  { value: 'LINE_MANAGER', label: 'Line Manager' },
  { value: 'POSITION', label: 'Position' },
  { value: 'JOB_LEVEL', label: 'Job Level' },
]);

const HIERARCHY_KINDS = new Set(
  PARTICIPANT_TYPE_OPTIONS.filter((o) => o.isHierarchy).map((o) => o.value)
);

export function isHierarchyKind(kind) {
  return HIERARCHY_KINDS.has(kind);
}

export const ROUTING_OPTIONS = Object.freeze([
  { value: 'sequential', label: 'Sequential' },
  { value: 'parallel', label: 'Parallel' },
]);

export const RESPONSE_TYPE_OPTIONS = Object.freeze([
  { value: 'required', label: 'Required' },
  { value: 'fyi', label: 'FYI' },
]);

const PARTICIPANT_LABELS = Object.fromEntries(
  PARTICIPANT_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

export function participantLabel(kind) {
  return PARTICIPANT_LABELS[kind] || kind;
}

/** Map a tenant actor token to a draft approver { kind, ref }. */
export function actorToApprover(actor) {
  const token = String(actor || '').trim();
  if (!token) return null;
  if (/^line\s*manager$/i.test(token) || /linemanager|management\s*hierarchy/i.test(token)) {
    return { kind: 'managementHierarchy', ref: '', levels: 1, hierarchyType: 'LINE_MANAGER' };
  }
  if (/auto\s*approve/i.test(token)) return { kind: 'selfAutoApprove', ref: '' };
  if (/represent/i.test(token)) return { kind: 'representative', ref: token };
  // HR_ABSENCE, approval groups, named roles → approvalGroup
  return { kind: 'approvalGroup', ref: token };
}

/** A blank THEN stage for the stages editor. */
export function blankStage(order = 1) {
  return {
    order,
    routing: 'sequential',
    responseType: 'required',
    approvers: [{ kind: 'managementHierarchy', ref: '', levels: 1, hierarchyType: 'LINE_MANAGER' }],
  };
}

/**
 * Convert a read-only snapshot rule (name + THEN participant-type chain) into an
 * editable draft `design` so the user can copy an existing rule, tweak it, and deploy.
 * Each THEN entry becomes a sequential stage; IF seeds from the captured detail when present.
 */
export function snapshotRuleToDesign(rule, task = {}, category = 'absence') {
  const taskName = task?.name || 'Approval rule';
  // Map snapshot palette kinds → editor participant kinds (palette uses plural forms).
  const kindMap = { users: 'user', approvalGroups: 'approvalGroup' };
  const thenChain = Array.isArray(rule?.then) && rule.then.length ? rule.then : ['managementHierarchy'];
  const refs = Array.isArray(rule?.thenRefs) ? rule.thenRefs : [];
  // Per-stage specs when present, otherwise the single thenSpec applies to the first stage.
  const stageSpecs = Array.isArray(rule?.thenStages) && rule.thenStages.length ? rule.thenStages : null;
  const normResp = (rt) => (/fyi/i.test(String(rt || '')) ? 'fyi' : 'required');

  const stages = thenChain.map((kind, i) => {
    const mapped = kindMap[kind] || kind;
    const spec = stageSpecs ? stageSpecs[i] : i === 0 ? rule?.thenSpec : null;
    // thenRefs align 1:1 with the THEN chain (e.g. representative type "HD_HRBP",
    // or a comma-joined Users list for a single stage). Fall back to spec participants.
    let ref = refs[i] != null ? refs[i] : '';
    if (!ref && spec?.participants) {
      const p = spec.participants;
      ref = p.users || p.groups || p.applicationRole || '';
      if (ref === 'null') ref = '';
    }
    const approver = { kind: mapped, ref };
    if (isHierarchyKind(mapped)) {
      approver.levels =
        spec?.levels != null ? Number(spec.levels) : rule?.levels != null ? Number(rule.levels) : 1;
      approver.hierarchyType = rule?.hierarchyType || 'LINE_MANAGER';
    }
    const stage = {
      order: i + 1,
      routing: 'sequential',
      responseType: spec ? normResp(spec.responseType) : 'required',
      approvers: [approver],
    };
    // Carry the captured Fusion list-builder / auto-action detail so the editor shows it.
    if (spec) {
      if (spec.listBuilder) stage.listBuilder = spec.listBuilder;
      if (spec.startingParticipant) stage.startingParticipant = spec.startingParticipant;
      if (spec.topParticipant) stage.topParticipant = spec.topParticipant;
      if (spec.autoActionEnabled != null) stage.autoActionEnabled = spec.autoActionEnabled;
      if (spec.autoAction != null && spec.autoAction !== 'null') stage.autoAction = spec.autoAction;
    }
    return stage;
  });

  let conditions;
  if (rule?.ifDetail?.statusCode) {
    conditions = [
      {
        description: `absenceDispStatusCd = "${rule.ifDetail.statusCode}"`,
        attribute: 'absenceDispStatusCd',
        operator: '==',
        value: rule.ifDetail.statusCode,
      },
    ];
  } else if (rule?.ifText) {
    conditions = [{ description: rule.ifText, attribute: '', operator: '', value: '' }];
  } else {
    conditions = [{ description: '', attribute: '', operator: '>', value: '' }];
  }

  const ruleEntry = { name: `${rule?.name || 'Rule'} (copy)`, conditions, stages };
  return {
    summary: `Copy of ${rule?.name || 'rule'} for ${taskName}`,
    transactionHint: taskName,
    transactionCategory: category,
    rules: [ruleEntry],
    conditions,
    stages,
    elseAction: { routing: 'selfAutoApprove', stages: [] },
    _copiedFrom: rule?.name || null,
  };
}

/** Build one editable rule { name, conditions[], stages[] } from a tenant segment. */
function ruleFromSegment(segment, index, metaCondition) {
  const seg = segment || {};
  let conditions;
  if (metaCondition && (metaCondition.field || metaCondition.operator || metaCondition.value)) {
    conditions = [
      {
        description: metaCondition.badgeLabel || metaCondition.raw || seg.condition || '',
        attribute: metaCondition.field || '',
        operator: metaCondition.operator || '>',
        value: metaCondition.value != null ? String(metaCondition.value) : '',
      },
    ];
  } else {
    conditions = [parseConditionToParts(seg.condition, seg.liveCondition)];
  }

  const approvers = (seg.actors || ['LineManager']).map(actorToApprover).filter(Boolean);
  const stages = [
    {
      order: 1,
      routing: seg.actionType === 'ParallelApproval' ? 'parallel' : 'sequential',
      responseType: 'required',
      approvers: approvers.length
        ? approvers
        : [{ kind: 'managementHierarchy', ref: '', levels: 1, hierarchyType: 'LINE_MANAGER' }],
    },
  ];

  return {
    name: seg.ruleName || seg.condition || `Rule ${index + 1}`,
    conditions,
    stages,
  };
}

/** A blank editable rule for "Add rule" in the authoring drawer. */
export function blankRule(index = 0) {
  return {
    name: `Rule ${index + 1}`,
    conditions: [{ description: '', attribute: '', operator: '>', value: '' }],
    stages: [
      {
        order: 1,
        routing: 'sequential',
        responseType: 'required',
        approvers: [{ kind: 'managementHierarchy', ref: '', levels: 1, hierarchyType: 'LINE_MANAGER' }],
      },
    ],
  };
}

/**
 * Seed an editable draft `design` from tenant rule segments + task.
 * Produces the IF/THEN ruleset model used by the authoring drawer + push pipeline:
 *   { summary, transactionHint, transactionCategory, rules[], conditions[], stages[], elseAction }
 * Every segment becomes a rule; top-level conditions/stages mirror the first rule so
 * single-rule consumers (simulate/compare/validate) keep working.
 */
export function buildDesignFromSegments(segments, meta, taskQuery = {}, category = 'absence') {
  const list = Array.isArray(segments) && segments.length ? segments : [{}];
  const taskName = meta?.task?.name || taskQuery?.name || 'Approval rule';
  const metaConditions = meta?.conditions || meta?.arrays?.conditions || [];

  const rules = list.map((seg, i) => ruleFromSegment(seg, i, metaConditions[i]));
  const first = rules[0] || blankRule(0);

  return {
    summary: `Approval rules for ${taskName}`,
    transactionHint: taskName,
    transactionCategory: category,
    rules,
    // Mirror the first rule at the top level for backward compatibility.
    conditions: first.conditions,
    stages: first.stages,
    elseAction: { routing: 'autoApprove', stages: [] },
    _seededFrom: {
      taskKey: meta?.task?.taskKey || taskQuery?.taskKey || null,
      code: meta?.task?.code || taskQuery?.code || null,
      rulesetName: resolveRulesetName(meta, taskQuery),
      source: meta?.source || null,
    },
  };
}
